import {
    Align,
    Color,
    MaxWidth,
    TypographyAlign,
    TypographyColor,
    TypographyMaxWidth,
    alignOptions,
} from "../components/typography"
import {
    Background,
    BackgroundColor,
    Container,
    ContainerType,
    Spacer,
    SpacerVariant,
    backgroundOptions,
    containerTypeOptions,
    spacerOptions,
} from "../components/layout"
import { Repeater, types } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../Typography"

import { ConditionalWrapper } from "../components/util"
import React from "react"
import { TextLinkBrickProps } from "./TextLink"
import { TextLinkColor } from "../components/text"
import classNames from "classnames"
import { iconOptionsWithPictograms, options, Pictograms } from "../../types"
import { IconText } from "../components/icon-text"

interface BasicTextProps {
    background: { color: BackgroundColor } | BackgroundColor
    align: TypographyAlign
    text: TypographyElement[]
    spacerTop: boolean
    spacerBottom: boolean
    variantTop?: SpacerVariant
    variantBottom?: SpacerVariant
    id?: string
    box?: boolean
    boxOutline?: boolean
    showIcon: boolean
    icon: Pictograms
    type?: ContainerType
}

const BasicText: types.Brick<BasicTextProps> = ({
    background,
    align,
    spacerTop,
    spacerBottom,
    variantTop,
    variantBottom,
    id,
    box,
    boxOutline,
    showIcon,
    icon,
    type,
}) => {
    const bgColor = typeof background === "object" ? background.color : background

    return (
        <Background color={bgColor}>
            <Spacer variant={!spacerTop ? SpacerVariant.None : variantTop} />
            <Container type={type}>
                <ConditionalWrapper
                    condition={!!(box || boxOutline)}
                    wrapper={children => (
                        <div
                            className={classNames("rounded-base p-7 sm:p-10 lg:p-16", {
                                "bg-white": box && bgColor !== BackgroundColor.White,
                                "bg-background-tide": box && bgColor === BackgroundColor.White,
                                border: boxOutline,
                                "border-border-tide": boxOutline && bgColor === BackgroundColor.White,
                                "border-dark-grey": boxOutline && bgColor === BackgroundColor.Grey,
                                "border-white":
                                    boxOutline &&
                                    (bgColor === BackgroundColor.Green || bgColor === BackgroundColor.DarkBlumine),
                            })}
                        >
                            {children}
                        </div>
                    )}
                >
                    <Color
                        color={
                            box
                                ? TypographyColor.Darkgrey
                                : bgColor === BackgroundColor.Green || bgColor === BackgroundColor.DarkBlumine
                                ? TypographyColor.White
                                : TypographyColor.Darkgrey
                        }
                    >
                        <IconText icon={icon} className={classNames("mb-5", { hidden: !showIcon })} />
                        <Align align={align}>
                            <MaxWidth
                                lgMaxWidth={
                                    type === ContainerType.Article ? TypographyMaxWidth.Full : TypographyMaxWidth["70%"]
                                }
                                className="space-y-7 md:space-y-10"
                            >
                                <Typography
                                    id={id}
                                    block={type === ContainerType.Article ? "copytext-article" : "copytext-large"}
                                    allowList={[
                                        "h1",
                                        "h2",
                                        "h3",
                                        "h4",
                                        "h5",
                                        type === ContainerType.Article ? "articleUl" : "ul",
                                        "small",
                                        "sup",
                                        "link",
                                        "bold",
                                        "italic",
                                        "quote",
                                        "checkList",
                                    ]}
                                />
                                <Repeater
                                    propName="repeater"
                                    itemProps={
                                        {
                                            color:
                                                bgColor === BackgroundColor.Green ||
                                                bgColor === BackgroundColor.DarkBlumine
                                                    ? TextLinkColor.White
                                                    : TextLinkColor.Blue,
                                        } as TextLinkBrickProps
                                    }
                                    renderWrapper={children => (
                                        <div className="flex flex-col space-y-5 sm:space-y-7">{children}</div>
                                    )}
                                />
                            </MaxWidth>
                        </Align>
                    </Color>
                </ConditionalWrapper>
            </Container>
            <Spacer variant={!spacerBottom ? SpacerVariant.None : variantBottom} />
        </Background>
    )
}

BasicText.schema = {
    name: "BasicText",
    label: "Basic Text",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/BasicText.png`,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "Button",
                    label: "Button",
                    max: 1,
                },
                {
                    type: "TextLink",
                    label: "Text Link",
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "id",
            label: "ID",
            type: types.SideEditPropType.Text,
        },
        {
            name: "type",
            label: "Type",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: containerTypeOptions,
                display: types.OptionsDisplay.Radio,
            },
        },
        {
            name: "align",
            label: "Align",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: alignOptions,
                display: types.OptionsDisplay.Select,
            },
        },
        {
            name: "background",
            label: "Background",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: backgroundOptions,
                display: types.OptionsDisplay.Color,
            },
        },
        {
            name: "box",
            label: "Box",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "boxOutline",
            label: "Box-Outline",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "showIcon",
            label: "Show Icon",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "icon",
            label: "Icon",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: iconOptionsWithPictograms,
                display: types.OptionsDisplay.Select,
            },
            show: props => props.showIcon,
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantTop",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptions,
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerTop === true,
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantBottom",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptions,
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerBottom === true,
                },
            ],
        },
    ],
    getDefaultProps: (): BasicTextProps => ({
        align: TypographyAlign.Left,
        background: { color: BackgroundColor.White },
        text: [
            {
                type: "h1",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet",
                    },
                ],
            },
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc placerat sagittis faucibus.",
                    },
                ],
            },
        ],
        spacerTop: true,
        spacerBottom: true,
        variantTop: SpacerVariant.Base,
        variantBottom: SpacerVariant.Base,
        box: false,
        boxOutline: false,
        showIcon: false,
        icon: Pictograms.vitality,
    }),
}

export default BasicText
